import React, { useMemo, useState, useCallback } from "react"
import PropTypes from "prop-types"
import uniqBy from "lodash.uniqby"
import { Button, Form, Select } from "antd"
import { useOutletContext } from "react-router-dom"

import { getConfig } from "@components/Config"
import { ListAllProjectsOutputShape } from "@api/services/backstage"

import { W2 } from "@components"

const consoleOrganizationId = getConfig("consoleOrganizationId")

const LABEL_CONTINUE = "Continue"
const LABEL_PROJECTS = "Projects"
const LABEL_ORGANIZATIONS = "Organizations"

const getProjectSlug = name => name
  .replace(/[^a-zA-Z0-9\s-]/g, '')  // Remove special characters except alphanumeric, spaces, and dashes
  .replace(/\s+/g, ' ')  // Replace multiple spaces with single space
  .replace(/\s/g, '-')   // Replace spaces with dashes
  .toUpperCase()

const ReportFiltersForm = ({
  projects = [],
  renderExtraInput = null,
  onSubmit,
}) => {
  const [ form ] = Form.useForm()
  const { organizations } = useOutletContext()

  const [ selectedOrganizationIds, setSelectedOrganizationIds ] = useState([])

  const organizationOptions = useMemo(() =>
    organizations
      .filter(({ id }) => id !== consoleOrganizationId)
      .filter(({ isDisabled }) => isDisabled !== true)
      .map(({ name: label, id: value }) => ({ label, value }))
  , [ organizations ])

  const isProjectAvailable = useCallback(project => {
    const { upid } = project
    const [ organizationId ] = upid.split('-')

    const hasSelectedOrganizations = selectedOrganizationIds.length > 0;

    if (!hasSelectedOrganizations) {
      return true
    }

    return selectedOrganizationIds.includes(organizationId)
  }, [ selectedOrganizationIds ])

  const projectOptions = useMemo(() =>
    uniqBy(
      (projects || [])
        .filter(isProjectAvailable)
        .map(({ name }) => ({
          label: name,
          value: getProjectSlug(name)
        }))
    , 'value')
  , [ projects, isProjectAvailable ])

  const onFinish = ({ organizationIds = [], projectSlugs = [], ...otherFilters }) => {
    const hasProjectsFilter = projectSlugs.length > 0
    const hasOrganizationsFilter = organizationIds.length > 0

    const filterOrganizations = targetOrganizations => {
      return (
        !hasOrganizationsFilter
          ? targetOrganizations
          : targetOrganizations.filter(({ id }) => organizationIds.includes(id))
      )
    }

    const filterProjects = targetProjects => {
      return (
        !hasProjectsFilter
          ? targetProjects
          : targetProjects.filter(({ name }) => projectSlugs.includes(getProjectSlug(name)))
      )
    }

    return onSubmit(filterOrganizations, filterProjects, otherFilters)
  }

  const formProps = {
    style: { marginTop: W2 },
    labelCol: { span: 6 },
    wrapperCol: { span: 18 },
    form,
    onFinish,
  }

  const buttonProps = {
    type: "primary",
    style: { marginLeft: 124 },
    htmlType: "submit",
  }

  const filterOption = (input, option) =>
    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())

  return (
    <Form {...formProps}>
      <Form.Item name="organizationIds" label={LABEL_ORGANIZATIONS}>
        <Select
          mode="multiple"
          options={organizationOptions}
          onChange={setSelectedOrganizationIds}
          placeholder="All organizations"
          filterOption={filterOption}
          allowClear
        />
      </Form.Item>

      <Form.Item name="projectSlugs" label={LABEL_PROJECTS}>
        <Select
          mode="multiple"
          options={projectOptions}
          onChange={() => {}}
          placeholder="All projects"
          filterOption={filterOption}
          allowClear
        />
      </Form.Item>

      {
        renderExtraInput && (
          renderExtraInput(form)
        )
      }

      <Form.Item label={null}>
        <Button {...buttonProps}>
          {LABEL_CONTINUE}
        </Button>
      </Form.Item>
    </Form>
  )
}

ReportFiltersForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  projects: PropTypes.arrayOf(ListAllProjectsOutputShape),
  renderExtraInput: PropTypes.func,
}

export default ReportFiltersForm
